/**
 * Глобальні дефолти користувача (рішення власника 2026-07-25):
 *
 * 1. **Організація за замовчуванням** — обирається у шапці застосунку.
 *    Всі сторінки/редактори, що підставляють «першу організацію»
 *    (availableOrgs[0]), автоматично отримують саме її: хук
 *    usePermissionsByOrg сортує список так, щоб дефолтна була першою.
 *
 * 2. **Період за замовчуванням** — місяць / квартал / рік / свій період /
 *    усі дати. Сторінки-списки документів фільтрують перелік за цим
 *    періодом (хук useDefaultPeriod → {from, to}).
 *
 * Персист: localStorage З КЛЮЧЕМ ПО TENANT (перемикання клієнта —
 * switchTenant з фільтра «клієнт» у шапці — робить повне перезавантаження;
 * дефолти не мають «протікати» між тенантами одного браузера).
 */
import { useState } from 'react';
import { create } from 'zustand';
import { useAuth } from '@/auth/store';

export type PeriodMode = 'all' | 'month' | 'quarter' | 'year' | 'custom';

export interface DefaultsState {
  defaultOrgId: string;          // '' = не задано (поведінка як раніше)
  periodMode: PeriodMode;
  customFrom: string;            // YYYY-MM-DD (лише для mode='custom')
  customTo: string;
  // 2026-08-09 (рішення власника): місяць/квартал/рік ОБИРАЮТЬСЯ, а не
  // завжди поточні. Дефолти — поточні.
  periodYear: number;
  periodMonth: number;           // 1..12 (для mode='month')
  periodQuarter: number;         // 1..4 (для mode='quarter')
  setDefaultOrg: (orgId: string) => void;
  setPeriodMode: (mode: PeriodMode) => void;
  setCustomRange: (from: string, to: string) => void;
  setPeriodYear: (y: number) => void;
  setPeriodMonth: (m: number) => void;
  setPeriodQuarter: (q: number) => void;
}

function tenantKey(suffix: string, tenantId?: string): string {
  const t = tenantId ?? useAuth.getState().activeTenantId ?? 'no-tenant';
  return `defaults.${t}.${suffix}`;
}

function read(suffix: string, fallback: string): string {
  try {
    return localStorage.getItem(tenantKey(suffix)) ?? fallback;
  } catch {
    return fallback;
  }
}

function write(suffix: string, value: string): void {
  try {
    localStorage.setItem(tenantKey(suffix), value);
  } catch { /* quota — ігноруємо */ }
}

export const useDefaults = create<DefaultsState>((set) => ({
  defaultOrgId: read('org_id', ''),
  periodMode: (read('period_mode', 'all') as PeriodMode),
  customFrom: read('period_from', ''),
  customTo: read('period_to', ''),
  periodYear: Number(read('period_year', String(new Date().getFullYear()))),
  periodMonth: Number(read('period_month', String(new Date().getMonth() + 1))),
  periodQuarter: Number(read('period_quarter',
    String(Math.floor(new Date().getMonth() / 3) + 1))),
  setDefaultOrg: (orgId) => {
    write('org_id', orgId);
    set({ defaultOrgId: orgId });
  },
  setPeriodMode: (mode) => {
    write('period_mode', mode);
    set({ periodMode: mode });
  },
  setCustomRange: (from, to) => {
    write('period_from', from);
    write('period_to', to);
    set({ customFrom: from, customTo: to });
  },
  setPeriodYear: (y) => {
    write('period_year', String(y));
    set({ periodYear: y });
  },
  setPeriodMonth: (m) => {
    write('period_month', String(m));
    set({ periodMonth: m });
  },
  setPeriodQuarter: (q) => {
    write('period_quarter', String(q));
    set({ periodQuarter: q });
  },
}));

/**
 * Організація за замовчуванням для ІНШОГО кабінету — перед перемиканням у
 * нього (ч.1068, розумний перемикач): після перезавантаження сторінки
 * useDefaults прочитає її вже як дефолт нового активного кабінету.
 */
export function setDefaultOrgForTenant(tenantId: string, orgId: string): void {
  try {
    localStorage.setItem(tenantKey('org_id', tenantId), orgId);
  } catch { /* quota — ігноруємо */ }
}

/** Межі періоду за замовчуванням: {from, to} у YYYY-MM-DD або null (усі дати). */
export function periodRange(
  mode: PeriodMode, customFrom: string, customTo: string,
  sel?: { year?: number; month?: number; quarter?: number },
): { from: string | null; to: string | null } {
  const now = new Date();
  const y = sel?.year ?? now.getFullYear();
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  switch (mode) {
    case 'month': {
      const m = (sel?.month ?? now.getMonth() + 1) - 1;
      const from = new Date(y, m, 1);
      const to = new Date(y, m + 1, 0);
      return { from: iso(from), to: iso(to) };
    }
    case 'quarter': {
      const q = (sel?.quarter ?? Math.floor(now.getMonth() / 3) + 1) - 1;
      const from = new Date(y, q * 3, 1);
      const to = new Date(y, q * 3 + 3, 0);
      return { from: iso(from), to: iso(to) };
    }
    case 'year':
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'custom':
      return { from: customFrom || null, to: customTo || null };
    default:
      return { from: null, to: null };
  }
}

/** Хук для сторінок-списків: параметри date_from/date_to для API (або undefined). */
export function useDefaultPeriod(): {
  dateFrom: string | undefined; dateTo: string | undefined;
  periodMode: PeriodMode;
} {
  const { periodMode, customFrom, customTo,
    periodYear, periodMonth, periodQuarter } = useDefaults();
  const { from, to } = periodRange(periodMode, customFrom, customTo,
    { year: periodYear, month: periodMonth, quarter: periodQuarter });
  return {
    // Backend list-endpoints приймають datetime — межі дня.
    dateFrom: from ? `${from}T00:00:00` : undefined,
    dateTo: to ? `${to}T23:59:59` : undefined,
    periodMode,
  };
}


/**
 * Фільтр «Організація» сторінки-списку: доки не обрано вручну — слідує за
 * «організацією за замовчуванням» з шапки. Ручний вибір (включно з
 * «Усі організації» = '') — override до кінця життя сторінки.
 */
export function useOrgFilter(): [string, (v: string) => void] {
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const [override, setOverride] = useState<string | null>(null);
  return [override ?? defaultOrgId, (v) => setOverride(v)];
}

/**
 * Нереактивний вибір організації для ІНІЦІАЛІЗАЦІЇ форм/фільтрів:
 * організація за замовчуванням з шапки (якщо є у списку) або перша зі
 * списку. Для реактивних сценаріїв — usePermissionsByOrg (defaultFirst).
 */
export function pickDefaultOrg<T extends { id: string }>(
  items: T[] | undefined,
): T | undefined {
  const d = useDefaults.getState().defaultOrgId;
  return items?.find((o) => o.id === d) ?? items?.[0];
}
